import { ref, computed, onMounted, onUnmounted } from 'vue';

const WINDOW_STATE = {
  OPEN: 'open',
  CLOSING: 'closing',
  URGENT: 'urgent',
  CLOSED: 'closed',
  NONE: 'none',
};

const CLOSING_THRESHOLD_MS = 60 * 60 * 1000; // 1 hour
const URGENT_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

export function useMessageWindow(conversation) {
  const now = ref(Date.now());
  let timer = null;

  const expiresAtMs = computed(() => {
    const val = conversation.value?.messaging_window_expires_at;
    return val ? val * 1000 : null;
  });

  const hasWindow = computed(() => expiresAtMs.value !== null);

  const remainingMs = computed(() => {
    if (!expiresAtMs.value) return null;
    return Math.max(0, expiresAtMs.value - now.value);
  });

  const state = computed(() => {
    if (!hasWindow.value) return WINDOW_STATE.NONE;
    if (remainingMs.value <= 0) return WINDOW_STATE.CLOSED;
    if (remainingMs.value <= URGENT_THRESHOLD_MS) return WINDOW_STATE.URGENT;
    if (remainingMs.value <= CLOSING_THRESHOLD_MS) return WINDOW_STATE.CLOSING;
    return WINDOW_STATE.OPEN;
  });

  // Returns an object with formatting hint so the template can pick the right i18n key
  // { kind: 'hours', hours, minutes } | { kind: 'minutes', minutes } | { kind: 'urgent', time }
  const remainingParts = computed(() => {
    if (!remainingMs.value || remainingMs.value <= 0) return null;
    const totalSeconds = Math.floor(remainingMs.value / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (state.value === WINDOW_STATE.URGENT) {
      const mm = String(minutes).padStart(2, '0');
      const ss = String(seconds).padStart(2, '0');
      return { kind: 'urgent', time: `${mm}:${ss}` };
    }
    if (hours > 0) {
      return { kind: 'hours', hours, minutes };
    }
    return { kind: 'minutes', minutes };
  });

  const formattedTime = computed(() => {
    if (!remainingMs.value || remainingMs.value <= 0) return '';
    const totalSeconds = Math.floor(remainingMs.value / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m`;
    const seconds = totalSeconds % 60;
    return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
  });

  const borderClass = computed(() => {
    switch (state.value) {
      case WINDOW_STATE.OPEN:
        return 'border-green-500';
      case WINDOW_STATE.CLOSING:
        return 'border-yellow-500';
      case WINDOW_STATE.URGENT:
        return 'border-red-500';
      case WINDOW_STATE.CLOSED:
        return 'border-red-500';
      default:
        return 'border-transparent';
    }
  });

  onMounted(() => {
    timer = setInterval(() => {
      now.value = Date.now();
    }, 1000);
  });

  onUnmounted(() => {
    if (timer) clearInterval(timer);
  });

  return {
    hasWindow,
    state,
    remainingMs,
    remainingParts,
    formattedTime,
    borderClass,
    WINDOW_STATE,
  };
}
